import { createContext, useContext, useState, useEffect } from 'react';

/**
 * Modern store access (React.createContext + hook), the supported replacement for
 * mobx-react's legacy `Provider`/`inject`. The value is the application `rootStore`,
 * mirroring what `<Provider rootStore={rootStore}>` exposed — so `useStores()`
 * returns `rootStore` and callers read e.g. `useStores().timeZoneStore`.
 *
 * Function components use the `useStores()` hook; class components that haven't been
 * converted yet can read `static contextType = StoreContext` -> `this.context`.
 *
 * The mobx-react `<Provider>` still renders alongside this during migration, so both
 * `@inject`/`inject(...)` and `useStores()` work simultaneously.
 */
export const StoreContext = createContext(null);

export function useStores() {
    const rootStore = useContext(StoreContext);
    if (!rootStore) {
        throw new Error('useStores() must be used inside <StoreContext.Provider>');
    }
    return rootStore;
}

/**
 * Owns a view store's lifecycle inside a function component — the hooks-era
 * counterpart to the `setCurrentView` HOC used by routed pages. The factory runs
 * exactly once per mount (via useState's lazy initializer, which — unlike useMemo —
 * is guaranteed not to be re-run), so transient state on the store survives
 * re-renders, and `destroy()` is called on unmount.
 *
 * Usage: `const viewStore = useViewStore(() => new FeatureViewStore(rootStore));`
 */
export function useViewStore(factory) {
    const [store] = useState(factory);
    useEffect(() => () => {
        if (store && typeof store.destroy === 'function') {
            store.destroy();
        }
    }, [store]);
    return store;
}
